import React from "react";
import { LegalLayout, Section, P, BulletList, Callout } from "./shared";

export default function TermsOfService() {
  return (
    <LegalLayout
      title="Terms of Service"
      subtitle="The terms on which you may use the Xenwealth website and engage our advisory services."
      meta={[
        ["Effective", "1 January 2026"],
        ["Last updated", "6 September 2026"],
        ["Governing law", "Malaysia"],
      ]}
      crossHref="/privacy"
      crossLabel="Privacy Policy"
      ctaHref="/#contact"
      ctaEyebrow="Questions about these terms"
      ctaLabel="Book a consultation"
      tocItems={[
        ["t1", "1. Acceptance of terms"],
        ["t2", "2. Nature of our services"],
        ["t3", "3. No investment guarantee"],
        ["t4", "4. Your responsibilities"],
        ["t5", "5. Engagement & fees"],
        ["t6", "6. Website use"],
        ["t7", "7. Intellectual property"],
        ["t8", "8. Third-party links"],
        ["t9", "9. Limitation of liability"],
        ["t10", "10. Confidentiality"],
        ["t11", "11. Termination"],
        ["t12", "12. Governing law"],
        ["t13", "13. Xenwealth Habit Tracker & Google Calendar integration"],
        ["t14", "14. Contact us"],
      ]}
    >
      <Callout>Nothing on this website is a personal recommendation or an offer to buy or sell any financial product. Advice is only given after a formal engagement and a review of your circumstances.</Callout>

      <Section id="t1" heading="1. Acceptance of terms">
        <P last>By accessing this website or engaging Xenwealth Group (“Xenwealth”, “we”, “us”), you agree to these terms. If you do not agree, please do not use the site or our services.</P>
      </Section>

      <Section id="t2" heading="2. Nature of our services">
        <P>We provide financial advisory and consulting services to individuals, families and businesses in Malaysia. The scope of any advice is defined in the engagement letter or service agreement we sign with you.</P>
        <P last>Content on this website is general in nature and does not take your personal objectives, financial situation or needs into account.</P>
      </Section>

      <Section id="t3" heading="3. No investment guarantee">
        <P last>All investments carry risk. Past performance is not a reliable indicator of future results, and the value of investments may fall as well as rise. We do not guarantee any particular return, outcome or tax result.</P>
      </Section>

      <Section id="t4" heading="4. Your responsibilities">
        <BulletList
          items={[
            "Give us complete and accurate information about your circumstances.",
            "Tell us promptly when your situation or objectives change materially.",
            "Read the product documents and disclosures we provide before deciding.",
            "Seek independent legal or tax advice where your situation calls for it.",
          ]}
        />
      </Section>

      <Section id="t5" heading="5. Engagement & fees">
        <P>An initial consultation does not create an advisory relationship. A relationship begins only when both parties sign an engagement letter setting out the scope of work, deliverables and fees.</P>
        <P last>Fees, commissions and any third-party remuneration will be disclosed to you in writing before work begins.</P>
      </Section>

      <Section id="t6" heading="6. Website use">
        <P last>You agree not to misuse this website — including attempting to gain unauthorised access, introducing malicious code, scraping content at scale, or using it for any unlawful purpose. We may suspend access where misuse is suspected.</P>
      </Section>

      <Section id="t7" heading="7. Intellectual property">
        <P last>The Xenwealth name, logo, text, graphics and materials on this site belong to Xenwealth Group or our licensors. You may view and print pages for personal reference, but may not reproduce, republish or use them commercially without our written consent.</P>
      </Section>

      <Section id="t8" heading="8. Third-party links">
        <P last>We may link to third-party websites for convenience. We do not control those sites and are not responsible for their content, products or privacy practices.</P>
      </Section>

      <Section id="t9" heading="9. Limitation of liability">
        <P last>To the fullest extent permitted by law, Xenwealth is not liable for indirect or consequential loss, or for loss arising from your reliance on general website content, from information you gave us that was inaccurate or incomplete, or from market movements outside our control. Nothing here excludes liability that cannot be excluded under Malaysian law.</P>
      </Section>

      <Section id="t10" heading="10. Confidentiality">
        <P last>We treat your information as confidential and handle it in accordance with our <a href="/privacy" style={{ color: "#b9860a", fontWeight: 600 }}>Privacy Policy</a>, disclosing it only as described there or as required by law.</P>
      </Section>

      <Section id="t11" heading="11. Termination">
        <P last>Either party may end an engagement by written notice as set out in the engagement letter. Fees for work already performed remain payable, and obligations of confidentiality survive termination.</P>
      </Section>

      <Section id="t12" heading="12. Governing law">
        <P last>These terms are governed by the laws of Malaysia, and the courts of Malaysia have exclusive jurisdiction over any dispute arising from them. We may update these terms from time to time; the current version is always the one posted on this page.</P>
      </Section>

      <Section id="t13" heading="13. Xenwealth Habit Tracker & Google Calendar integration">
        <P>Xenwealth Habit Tracker is an internal productivity and activity-management tool available to authorised Xenwealth team members for recording work activities such as appointments, meetings, recruitment activities and events. See our <a href="/habit-tracker" style={{ color: "#b9860a", fontWeight: 600 }}>Xenwealth Habit Tracker</a> page for more information about the platform.</P>
        <P>Google Calendar integration within Habit Tracker is optional. To connect your Google Calendar, you must authorise access through Google's OAuth consent process; Habit Tracker will not access your Calendar without this authorisation.</P>
        <P>Once connected, Habit Tracker uses the integration to import your selected Calendar events into Habit Tracker. Habit Tracker does not create, modify or delete events in your Google Calendar, and you remain responsible for the accuracy of information stored in your own Calendar.</P>
        <P>Synchronisation may not always be instantaneous, and temporary interruptions can occur due to Google service availability, your internet connection, expiry or revocation of OAuth authorisation, scheduled maintenance, or other technical reasons outside our reasonable control.</P>
        <P>You may disconnect or revoke Habit Tracker's access to your Google Calendar at any time, whether from within Habit Tracker or through your Google Account permissions. Continued availability of this and other third-party integrations depends on services provided by Google and may change over time.</P>
        <P last>For details of how Google Calendar information is used and retained, see our <a href="/privacy" style={{ color: "#b9860a", fontWeight: 600 }}>Privacy Policy</a>.</P>
      </Section>

      <Section id="t14" heading="14. Contact us">
        <P last>Questions about these terms can be sent to the details below.</P>
      </Section>
    </LegalLayout>
  );
}
